import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';

const Header = ({ onMenuToggle, onCollapseToggle, isSidebarCollapsed }) => {
  const { user } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [showSupportTooltip, setShowSupportTooltip] = useState(false);
  const [labStatus, setLabStatus] = useState({
    operational: true,
    lastUpdate: '5 min ago',
    activeTests: 42
  });
  const navigate = useNavigate();

  const handleProfileToggle = () => setIsProfileOpen(!isProfileOpen);

  const handleLogout = async () => {
    
    try {
      await axiosInstance.post(`${import.meta.env.VITE_API_BASE_URL}/api/users/logout`, {}, { withCredentials: true });
      localStorage.removeItem('token'); 
      navigate('/'); 
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Update current date and time every second
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const options = { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true 
      };
      const formattedDateTime = now.toLocaleDateString('en-US', options);
      setCurrentDateTime(formattedDateTime);
    };

    updateDateTime(); // Initial call
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && !event.target.closest('.profile-dropdown')) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-50 to-teal-50 border-b border-blue-100 shadow-sm">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        
        {/* LEFT SECTION - Compressed */}
        <div className="flex items-center flex-1 min-w-0">
          
          {/* MOBILE MENU BUTTON */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="lg:hidden text-blue-600 hover:bg-blue-100 mr-2"
          >
            <Icon name="Menu" size={20} />
          </Button>

          {/* Logo - Compact Version */}
          <div className="flex items-center min-w-0">
            {/* Logo Image - Adjust size */}
            <div className="w-32 mr-3 flex-shrink-0">
              <img src="/logo.png" alt="Novus Health Labs" className="h-14 w-auto object-contain" />
            </div>

            {/* Status for Desktop - Hidden on smaller screens */}
            <div className="hidden lg:flex items-center space-x-3 ml-3 pl-3 border-l border-blue-200 min-w-0">
              <div className="flex items-center space-x-2 whitespace-nowrap">
                <div className={`w-2 h-2 rounded-full ${labStatus.operational ? 'bg-green-500' : 'bg-red-500'} animate-pulse flex-shrink-0`}></div>
                <span className="text-sm font-medium text-blue-800 truncate">
                  All Systems Operational
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER SECTION - Date & Time */}
        <div className="hidden md:flex items-center mx-4">
          <div className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-lg border border-blue-200 shadow-sm">
            <Icon name="Calendar" size={14} className="text-blue-500" />
            <span className="text-sm font-medium text-blue-800 whitespace-nowrap">
              {currentDateTime}
            </span>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse ml-1"></div>
          </div>
        </div>

        {/* RIGHT SECTION - Now with proper spacing */}
        <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
          
          {/* Lab Reports Button - Show on medium+ screens */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/reports")}
            className="hidden md:flex items-center space-x-2 border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
          >
            <Icon name="FileText" size={16} />
            <span>Lab Reports</span>
          </Button>

          {/* HELP / SUPPORT with Tooltip */}
          <div className="relative">
            <Button 
              variant="ghost" 
              size="icon"
              onMouseEnter={() => setShowSupportTooltip(true)}
              onMouseLeave={() => setShowSupportTooltip(false)}
              onClick={() => window.open('mailto:support@novushealthlabs.com', '_blank')}
              className="text-blue-600 hover:bg-blue-100 flex-shrink-0 relative"
              title="Contact Support"
            >
              <Icon name="HelpCircle" size={18} />
            </Button>
            
            {/* Support Tooltip */}
            {showSupportTooltip && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-gray-900 text-white rounded-lg shadow-xl z-50 p-3 animate-fadeIn">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Phone" size={14} color="white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Nakshatra Namaha Creations Pvt Ltd</p>
                    <p className="text-xs text-gray-300 mt-1">Technical Support & Development</p>
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <p className="text-xs text-gray-400">📧 support@novushealthlabs.com</p>
                      <p className="text-xs text-gray-400">📞 +91-XXXXXXXXXX</p>
                      <p className="text-xs text-gray-400 mt-1">🕒 24/7 Support Available</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SETTINGS */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/settings")}
            className="text-blue-600 hover:bg-blue-100 flex-shrink-0"
            title="Settings"
          >
            <Icon name="Settings" size={18} />
          </Button>

          {/* PROFILE - Compact version */}
          <div className="relative profile-dropdown">
            <Button
              variant="ghost"
              onClick={handleProfileToggle}
              className="flex items-center space-x-2 px-2 py-1.5 hover:bg-blue-100 rounded-lg transition-colors"
            >
              {/* User Avatar */}
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-teal-500 rounded-full flex items-center justify-center shadow flex-shrink-0">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <Icon name="User" size={14} color="white" />
                )}
              </div>

              {/* User Info - Show on larger screens */}
              <div className="hidden lg:block text-left min-w-0 max-w-[120px]">
                <p className="text-sm font-semibold text-blue-900 truncate">{user?.name || 'Loading...'}</p>
                <div className="truncate">
                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200 truncate inline-block max-w-full">
                    {user?.role || 'Staff'}
                  </span>
                </div>
              </div>

              {/* Dropdown Chevron */}
              <Icon
                name="ChevronDown"
                size={16}
                className={`hidden lg:block text-blue-500 transition-transform flex-shrink-0 ${isProfileOpen ? 'rotate-180' : ''}`}
              />
            </Button>

            {isProfileOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-blue-100 rounded-lg shadow-xl z-50">
                <div className="p-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-teal-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-teal-500 rounded-full flex items-center justify-center shadow flex-shrink-0">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <Icon name="User" size={16} color="white" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-blue-900 truncate">{user?.name}</p>
                      <p className="text-xs text-blue-600 truncate">{user?.email}</p>
                      <div className="flex items-center mt-1 space-x-2">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                          <span className="text-xs text-blue-700">Active</span>
                        </div>
                        {user?.employeeId && (
                          <>
                            <div className="text-xs text-blue-400">•</div>
                            <div className="text-xs text-blue-500 truncate">ID: {user.employeeId}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-2 space-y-1">
                
                  {/* <Button variant="ghost" className="w-full justify-start text-sm px-3 py-2 text-blue-800 hover:bg-blue-50">
                    <Icon name="Users" size={16} className="mr-3 text-blue-500 flex-shrink-0" />
                    <span className="truncate">Patients</span>
                  </Button> */}
                  
                  <div className="border-t border-blue-100 pt-2 mt-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm px-3 py-2 text-red-600 hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <Icon name="LogOut" size={16} className="mr-3 flex-shrink-0" />
                      <span className="truncate">Sign Out</span>
                    </Button>
                  </div>
                </div>

                <div className="px-3 py-2 text-xs text-blue-500 border-t border-blue-100 bg-blue-50/50 rounded-b-lg">
                  <div className="flex justify-between items-center">
                    <div className="truncate">
                      <span className="font-medium truncate">novus HEALTH LABS</span>
                      <span className="text-blue-400 mx-1">•</span>
                      <span className="text-xs">Powered by Nakshatra Namaha Creations</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-600 font-medium text-xs">Secure</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* MOBILE DATE/TIME - Show below header on mobile */}
      <div className="md:hidden bg-blue-50 border-t border-blue-100 px-4 py-1">
        <div className="flex items-center justify-center space-x-2">
          <Icon name="Clock" size={12} className="text-blue-500" />
          <span className="text-xs font-medium text-blue-800">
            {currentDateTime}
          </span>
        </div>
      </div>
    </header>
  );
};

export default Header;